import {
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Collapse,
    colors,
    Divider,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Theme,
    Typography
} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import clsx from 'clsx';
import React from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import useRouter from 'use-react-router';
import {TableEditBar} from '../../../components/TableEditBar';
import {PaginatiorInfo} from '../../../graphql/common.datatypes';
import {Payment, PaymentStatuses, PaymentType, PaymentTypes} from '../../../graphql/payments.datatypes';
import {EST, formatDateTime, formatMoney} from '../../../utils/formatters';
import Box from '@material-ui/core/Box';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import getBillingParty from '../../BillingParty/getBillingParty.api';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        position: 'relative',
    },
    content: {
        padding: 0
    },
    inner: {
        [theme.breakpoints.up('md')]: {
            minWidth: 700,
        },
        maxWidth: '90vw'
    },
    nameCell: {
        display: 'flex',
        alignItems: 'center'
    },
    avatar: {
        height: 42,
        width: 42,
        marginRight: theme.spacing(1)
    },
    actions: {
        padding: theme.spacing(1),
        justifyContent: 'flex-end'
    },
    unbalancedPayment: {
        backgroundColor: colors.orange[100],
    },
    refundPayment: {
        backgroundColor: colors.lightGreen[100],
    },
}));

const Results = (props: {
    className?: string,
    payments: Payment[],
    paginatorInfo?: PaginatiorInfo,
    setPage?: (page: number) => void,
    rowsPerPage?: number,
    setRowsPerPage?: (rowsPerPage: number) => void,
    selectedPayment?: Payment,
    setSelectedPayment?: (payment: Payment) => void,
    isAdmin?: boolean
    billingPartyId?: number  // Add billingPartyId here
    incomingTransfers?: Payment[]  // Incoming transfers from other billing parties (for non-admin users)
}) => {
    const {
        className,
        rowsPerPage,
        setPage,
        setRowsPerPage,
        paginatorInfo,
        selectedPayment,
        setSelectedPayment,
        payments,
        isAdmin,
        billingPartyId,
        incomingTransfers,
        ...rest
    } = props;
    const classes = useStyles();
    const {history} = useRouter();

    // Separate payments into transfers and non-transfers
    // For non-admin users, also include incoming transfers from other billing parties
    const outgoingTransfers = payments.filter(payment => payment.payment_type === PaymentType.TRANSFER);
    const transferPayments = isAdmin
        ? outgoingTransfers
        : [...outgoingTransfers, ...(incomingTransfers || [])];
        

    const nonTransferPayments = payments.filter(payment => payment.payment_type !== PaymentType.TRANSFER);
    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        if (setPage) setPage(page + 1);
    };
    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (setRowsPerPage) setRowsPerPage(Number(event.target.value));
    };

    return (
        <div>
            <div
                {...rest}
                className={clsx(classes.root, className)}
            >
                <Card>
                    <CardHeader
                        title={'Payments'}
                    />
                    <Divider/>
                    <CardContent className={classes.content}>
                        <PerfectScrollbar>
                            <div className={classes.inner}>
                                <Table style={{width: '100%'}}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell/>
                                            {isAdmin && (<TableCell style={{width: 50}}>Id</TableCell>)}
                                            <TableCell style={{width: 215}}>Timestamp</TableCell>
                                            <TableCell>Paying Member</TableCell>
                                            <TableCell>Description</TableCell>
                                            <TableCell style={{width: 100}}>Amount</TableCell>
                                            <TableCell style={{width: 100}}>Assigned</TableCell>
                                            <TableCell style={{width: 100}}>Status</TableCell>
                                            <TableCell style={{width: 100}}>Type</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {billingPartyId ? (
                                            nonTransferPayments.map(payment => {
                                                if (payment && payment.billingPartyMember && payment.billingPartyMember.billing_party_id === billingPartyId) {
                                                    return (
                                                        <Row key={payment.id} payment={payment}
                                                             billingPartyId={billingPartyId} // Pass billingPartyId to Row
                                                             selectedPayment={selectedPayment}
                                                             setSelectedPayment={setSelectedPayment} isAdmin={isAdmin}/>
                                                    )
                                                }
                                            })
                                        ) : (
                                            payments && payments.map(payment => (
                                                <Row key={payment.id} payment={payment}
                                                     billingPartyId={billingPartyId}
                                                     selectedPayment={selectedPayment}
                                                     setSelectedPayment={setSelectedPayment} isAdmin={isAdmin}/>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        </PerfectScrollbar>
                    </CardContent>
                    {paginatorInfo && rowsPerPage && (
                        <CardActions className={classes.actions}>
                            <TablePagination
                                component="div"
                                count={paginatorInfo.total}
                                onChangePage={handleChangePage}
                                onChangeRowsPerPage={handleChangeRowsPerPage}
                                page={paginatorInfo.currentPage - 1}
                                rowsPerPage={rowsPerPage}
                                rowsPerPageOptions={[25, 50, 100]}
                            />
                        </CardActions>
                    )}
                </Card>
                <TableEditBar selected={[]}/>
            </div>

            {billingPartyId && (
                <div
                {...rest}
                className={clsx(classes.root, className)}
            >
                <Card>
                    <CardHeader
                        title={'Transfers'}
                    />
                    <Divider/>
                    <CardContent className={classes.content}>
                        <PerfectScrollbar>
                            <div className={classes.inner}>
                                <Table style={{width: '100%'}}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell/>
                                            {isAdmin && (<TableCell style={{width: 50}}>Id</TableCell>)}
                                            <TableCell style={{width: 140}}>Billing Party ID</TableCell>
                                            <TableCell style={{width: 215}}>Timestamp</TableCell>
                                            <TableCell>Paying Member</TableCell>
                                            <TableCell>Group Member</TableCell>
                                            <TableCell>Group Member ID</TableCell>
                                            <TableCell>Description</TableCell>
                                            <TableCell style={{width: 100}}>Amount</TableCell>
                                            <TableCell style={{width: 100}}>Status</TableCell>

                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {transferPayments.map(payment => {
                                            if (payment && payment.payment_type === PaymentType.TRANSFER && payment.groupMemberPayments && payment.billingPartyMember && payment.billingPartyMember.billing_party_id === billingPartyId) {
                                                return (
                                                    <Row
                                                        key={payment.id}
                                                        payment={payment}
                                                        billingPartyId={billingPartyId} // Pass billingPartyId to Row
                                                        selectedPayment={selectedPayment}
                                                        setSelectedPayment={setSelectedPayment}
                                                        isAdmin={isAdmin}
                                                    />
                                                );
                                            }

                                            if (payment && payment.payment_type === PaymentType.TRANSFER && payment.groupMemberPayments && payment.billingPartyMember && payment.billingPartyMember.billing_party_id !== billingPartyId) {
                                                return (
                                                    <GroupMemberRow
                                                        key={payment.id}
                                                        payment={payment}
                                                        billingPartyId={billingPartyId} // Pass billingPartyId to Row
                                                        selectedPayment={selectedPayment}
                                                        setSelectedPayment={setSelectedPayment}
                                                        isAdmin={isAdmin}
                                                    />
                                                )
                                            }

                                        })}
                                    </TableBody>

                                </Table>
                            </div>
                        </PerfectScrollbar>
                    </CardContent>
                    {paginatorInfo && rowsPerPage && (
                        <CardActions className={classes.actions}>
                            <TablePagination
                                component="div"
                                count={paginatorInfo.total}
                                onChangePage={handleChangePage}
                                onChangeRowsPerPage={handleChangeRowsPerPage}
                                page={paginatorInfo.currentPage - 1}
                                rowsPerPage={rowsPerPage}
                                rowsPerPageOptions={[25, 50, 100]}
                            />
                        </CardActions>
                    )}
                </Card>
            </div>
                )}
        </div>
    );
};

function Row(props: {
    payment: Payment,
    selectedPayment?: Payment,
    setSelectedPayment?: (payment: Payment) => void,
    isAdmin?: boolean
    billingPartyId?: number  // Add billingPartyId prop here
}) {
    const {payment, selectedPayment, setSelectedPayment, isAdmin, billingPartyId} = props;
    const [open, setOpen] = React.useState(false);
    const isRefund = payment.payment_type === PaymentType.REFUND_CHECK || payment.payment_type === PaymentType.REFUND_NEXIO;
    const isTransfer = payment.payment_type === PaymentType.TRANSFER;
    const classes = useStyles();
    const {history} = useRouter();

    const openPaymentModal = (payment: Payment) => setSelectedPayment ? setSelectedPayment(payment) : history.push('/payments/' + payment.id);

    const displayAssigned = isRefund
        ? -payment.amount!      // use the refund amount
        : payment.assignedAmount!;

    return (
        <React.Fragment>
            <TableRow
                hover
                key={payment.id}
                selected={selectedPayment === payment}
                className={clsx({
                    [classes.unbalancedPayment]: (payment.amount !== payment.assignedAmount && !isRefund),
                    [classes.refundPayment]: isRefund,
                })}
            >

                {isTransfer && payment.groupMemberPayments && payment.billingPartyMember && payment.billingPartyMember.billing_party_id === billingPartyId ? (
                    <TableCell>
                        <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                            {open ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
                        </IconButton>
                    </TableCell>
                ) : isTransfer && payment.groupMemberPayments && payment.billingPartyMember ? (
                    <TableCell/>
                ) : (
                    <span></span>
                )}

                {isAdmin && (
                    <TableCell onClick={() => openPaymentModal(payment)}>{payment.id}</TableCell>
                )}

                {isTransfer && payment.groupMemberPayments && payment.billingPartyMember && payment.billingPartyMember.billing_party_id === billingPartyId ? (
                    <>
                        <TableCell onClick={() => openPaymentModal(payment)}>{payment.billingPartyMember && payment.billingPartyMember.billing_party_id}</TableCell>
                        <TableCell onClick={() => openPaymentModal(payment)}>{formatDateTime(payment.timestamp, EST)}</TableCell>
                        <TableCell onClick={() => openPaymentModal(payment)}>{payment.billingPartyMember && payment.billingPartyMember.first_name + ' ' + payment.billingPartyMember.last_name}</TableCell>
                        <TableCell/>
                        <TableCell/>
                        <TableCell onClick={() => openPaymentModal(payment)}>{payment.description}</TableCell>
                        <TableCell onClick={() => openPaymentModal(payment)}>{isRefund ? '-' : formatMoney(payment.amount!, true)}</TableCell>
                        <TableCell onClick={() => openPaymentModal(payment)}>{PaymentStatuses[payment.payment_status!].name}</TableCell>
                    </>
                ) : (
                    <>
                        <TableCell onClick={() => openPaymentModal(payment)}>{formatDateTime(payment.timestamp, EST)}</TableCell>
                        <TableCell onClick={() => openPaymentModal(payment)}>{payment.billingPartyMember && payment.billingPartyMember.first_name + ' ' + payment.billingPartyMember.last_name}</TableCell>
                        <TableCell onClick={() => openPaymentModal(payment)}>{payment.description}</TableCell>
                        <TableCell onClick={() => openPaymentModal(payment)}>{formatMoney(isRefund ? -payment.amount! : payment.amount!, true)}</TableCell>
                        <TableCell onClick={() => openPaymentModal(payment)}>{formatMoney(displayAssigned, true)}</TableCell>
                        <TableCell onClick={() => openPaymentModal(payment)}>{PaymentStatuses[payment.payment_status!].name}</TableCell>
                        <TableCell onClick={() => openPaymentModal(payment)}>{PaymentTypes[payment.payment_type!].name}</TableCell>
                    </>
                )}
            </TableRow>

            {/* Collapsible section for groupMemberPayments */}
            {isTransfer && payment.groupMemberPayments && payment.billingPartyMember && payment.billingPartyMember.billing_party_id === billingPartyId && (
                <TableRow>
                    <TableCell style={{paddingBottom: 0, paddingTop: 0}} colSpan={8}>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                            <Box margin={1}>
                                <Typography variant="h6" gutterBottom component="div">
                                    Recipients
                                </Typography>
                                <Table size="small" aria-label="group member payments">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Id</TableCell>
                                            <TableCell align="left">Group Member ID</TableCell>
                                            <TableCell align="left">Member</TableCell>
                                            <TableCell align="right">Amount</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {payment.groupMemberPayments
                                            .map((groupMemberPayment) => (
                                                <TableRow key={groupMemberPayment.group_member_id}>
                                                    <TableCell>{groupMemberPayment.id}</TableCell>
                                                    <TableCell>{groupMemberPayment.group_member_id}</TableCell>
                                                    <TableCell>{groupMemberPayment.groupMember && `${groupMemberPayment.groupMember.first_name} ${groupMemberPayment.groupMember.last_name}`}</TableCell>
                                                    <TableCell
                                                        align="right">{formatMoney(groupMemberPayment.amount, true)}</TableCell>
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            )}

        </React.Fragment>
    );
}

function GroupMemberRow(props: {
    payment: Payment,
    selectedPayment?: Payment,
    setSelectedPayment?: (payment: Payment) => void,
    isAdmin?: boolean
    billingPartyId?: number  // Add billingPartyId prop here
}) {
    const {payment, selectedPayment, setSelectedPayment, isAdmin, billingPartyId} = props;
    const [open, setOpen] = React.useState(false);
    const isRefund = payment.payment_type === PaymentType.REFUND_CHECK || payment.payment_type === PaymentType.REFUND_NEXIO;
    const isTransfer = payment.payment_type === PaymentType.TRANSFER;
    const classes = useStyles();
    const {history} = useRouter();

    const openPaymentModal = (payment: Payment) => setSelectedPayment ? setSelectedPayment(payment) : history.push('/payments/' + payment.id);

    const { data } = getBillingParty(billingPartyId != null ? billingPartyId : null);
    const billingParty = data && data.billingParty;

    const billingPartyMemberIds: Array<number | undefined> = (billingParty && billingParty.members)
        ? billingParty.members.map(member => member.group_member_id)
        : [];

    return (
        <React.Fragment>
            {payment && payment.groupMemberPayments && (() => {
                let totalGroupMemberPaymentAmount = 0;

                payment.groupMemberPayments.forEach(groupMemberPayment => {
                    totalGroupMemberPaymentAmount += groupMemberPayment.amount;
                });

                console.log(`Payment ID: ${payment.id}, Payment Amount: ${payment.amount}, Assigned Amount: ${payment.assignedAmount}, Total Group Member Payment Amount: ${totalGroupMemberPaymentAmount}`);

                return (payment.groupMemberPayments || [])
                    .filter(groupMemberPayment => billingPartyMemberIds.includes(groupMemberPayment.group_member_id))
                    .map(groupMemberPayment => (
                        <TableRow
                            hover
                            key={payment.id}
                            selected={selectedPayment === payment}
                            className={clsx({
                                [classes.unbalancedPayment]: (payment.amount !== payment.assignedAmount && !isRefund),
                                [classes.refundPayment]: isRefund,
                            })}
                        >
                            <TableCell/>
                            {isAdmin && (<TableCell onClick={() => openPaymentModal(payment)}>{payment.id}</TableCell>)}
                            <TableCell onClick={() => openPaymentModal(payment)}>{payment.billingPartyMember && payment.billingPartyMember.billing_party_id}</TableCell>
                            <TableCell onClick={() => openPaymentModal(payment)}>{formatDateTime(payment.timestamp, EST)}</TableCell>
                            <TableCell onClick={() => openPaymentModal(payment)}>{payment.billingPartyMember && payment.billingPartyMember.first_name + ' ' + payment.billingPartyMember.last_name}</TableCell>
                            <TableCell onClick={() => openPaymentModal(payment)}>{groupMemberPayment.groupMember && `${groupMemberPayment.groupMember.first_name} ${groupMemberPayment.groupMember.last_name}`}</TableCell>
                            <TableCell onClick={() => openPaymentModal(payment)}>{groupMemberPayment.group_member_id}</TableCell>
                            <TableCell onClick={() => openPaymentModal(payment)}>{payment.description}</TableCell>
                            <TableCell onClick={() => openPaymentModal(payment)}>{isRefund ? '-' : formatMoney(groupMemberPayment.amount!, true)}</TableCell>
                            <TableCell onClick={() => openPaymentModal(payment)}>{PaymentStatuses[payment.payment_status!].name}</TableCell>
                        </TableRow>
                    ))
            })()}
        </React.Fragment>
    );
}


export default Results;
